import { AfterViewInit, Component, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { InputCustomEvent, IonModal, IonSearchbar, ModalController } from '@ionic/angular';
import {
    AddressResult,
    PoiAddressResult,
    ApplicationFacade,
    SearchResult,
    SearchService,
    SearchUtil,
    StopRouteResult,
    TransitNetworkPartType,
    OtpRecentSearchUtilService
} from '@ridango/components';
import { BehaviorSubject, combineLatest, forkJoin, Subject } from 'rxjs';
import Env from '@ridango/core/env';
import { switchMap, takeUntil, debounceTime, distinctUntilChanged, tap, filter, map } from 'rxjs/operators';
import { BaseComponent } from '@ridango/core';
import { KeyboardService } from '@ridango/core/lib/services/keyboard.service';

export enum ExploreSearchModalResultType {
    Confirm = 'confirm'
}

@Component({
    selector: 'rwidget-explore-search-modal',
    templateUrl: './explore-search-modal.component.html',
    styleUrls: ['./explore-search-modal.component.scss']
})
export class ExploreSearchModalComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
    @Output() itemSelected: EventEmitter<StopRouteResult | AddressResult | PoiAddressResult> = new EventEmitter<
        StopRouteResult | AddressResult | PoiAddressResult
    >();
    @ViewChild(IonModal) modal: IonModal;
    @ViewChild('exploreSearchBar') exploreSearchBar: IonSearchbar;

    searchPhrase$: Subject<string> = new Subject<string>();
    searchText: string;

    recentSearchItems: (AddressResult | StopRouteResult | PoiAddressResult)[];
    searchResultItems: SearchResult;
    sortedResult: SearchResult;
    searchLoading: boolean;
    hasSearched = false;
    selectedItem: StopRouteResult | AddressResult | PoiAddressResult;

    assetsPath$ = this.applicationFacade.assetsPath$;
    highContrast$ = this.applicationFacade.highContrast$;
    public TransitNetworkPartType = TransitNetworkPartType;

    readonly searchUtil = SearchUtil;
    readonly RECENT_KEY = 'explore';

    constructor(
        @Inject('ENV') public env: Env,
        private applicationFacade: ApplicationFacade,
        public searchService: SearchService,
        private keyboardService: KeyboardService,
        private modalCtrl: ModalController,
        private readonly otpRecentSearchUtilService: OtpRecentSearchUtilService
    ) {
        super();
    }

    ngOnInit(): void {
        this.initRecentSearch();
        this.initSearch();
    }

    ngAfterViewInit(): void {
        this.handleModalOpen();
    }

    ionViewDidEnter() {
        this.exploreSearchBar.setFocus();
    }

    async closeModal(): Promise<void> {
        this.clearData();
        await this.modalCtrl.dismiss(this.selectedItem, ExploreSearchModalResultType.Confirm);
    }

    handleModalOpen() {
        this.searchPhrase$.next('');
    }

    canShowMore(type: 'stopRoutes' | 'addresses' | 'poi'): boolean {
        return (
            this.searchResultItems?.[type]?.length < this.sortedResult?.[type]?.length &&
            this.env.SEARCH_SETTINGS?.SHOW_LOAD_MORE
        );
    }

    clearData() {
        this.searchPhrase$.next('');
    }

    handleSearchItemSelect(selectedItem: StopRouteResult | AddressResult | PoiAddressResult) {
        this.itemSelected.emit(selectedItem);
        this.selectedItem = selectedItem;

        forkJoin([
            this.otpRecentSearchUtilService.addToRecentSearch(
                selectedItem,
                this.RECENT_KEY,
                this.env?.BUS_STOPS_PREVIOUS_SEARCHES_LIMIT
            ),
            this.otpRecentSearchUtilService.addToCachedSearch(selectedItem, this.RECENT_KEY)
        ])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => {
                this.closeModal();
            });
    }

    clearRecentSearchItems(): void {
        this.otpRecentSearchUtilService
            .clearRecentSearchItems(this.RECENT_KEY)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => {
                this.recentSearchItems = [];
            });
    }

    handleSearchPhraseChange($event: InputCustomEvent) {
        this.searchPhrase$.next($event?.target?.value?.toString().trim().toLowerCase() ?? '');
    }

    private initRecentSearch() {
        this.searchPhrase$
            .pipe(
                filter((val) => !val),
                switchMap(() => this.otpRecentSearchUtilService.getRecentSearchItems(this.RECENT_KEY)),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe((items) => {
                this.recentSearchItems = items;
            });
    }

    private initSearch() {
        this.searchService.searchLoading
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((loading) => (this.searchLoading = Object.values(loading).some((value) => value)));
        this.searchPhrase$
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                filter((term) => term.length >= (this.env.SEARCH_SETTINGS?.MINIMUM_SEARCH_CHARACTERS || 1)),
                takeUntil(this.componentDestroyed$),
                switchMap((searchPhrase) => {
                    this.hasSearched = !!searchPhrase;
                    this.searchText = searchPhrase;
                    const type = this.searchUtil.startsWithNumber(searchPhrase, this.env)
                        ? TransitNetworkPartType.Route
                        : undefined;

                    const searchSubject = new BehaviorSubject<string>(searchPhrase);
                    return combineLatest([
                        this.searchService.InitSearch(searchSubject, undefined, type),
                        searchSubject.asObservable()
                    ]);
                })
            )
            .pipe(
                tap(() => {
                    this.searchService.updateSearchResultsAnnouncementMessage(this.searchResultItems);
                }),
                switchMap(([result, searchPhrase]) =>
                    this.otpRecentSearchUtilService
                        .getCachedSearchItems(this.RECENT_KEY)
                        .pipe(map((cachedSearchItems) => ({ result, searchPhrase, cachedSearchItems })))
                )
            )
            .subscribe(({ result, searchPhrase, cachedSearchItems }) => {
                this.sortedResult = this.searchUtil.sortArraysByRecentSearchItems(
                    result,
                    cachedSearchItems,
                    searchPhrase
                );
                if (this.searchUtil.startsWithNumber(searchPhrase, this.env)) {
                    this.searchResultItems = this.sortedResult;
                } else {
                    this.searchResultItems = this.env.SEARCH_SETTINGS
                        ? this.searchUtil.limitSearchResults(this.sortedResult, this.env)
                        : this.sortedResult;
                }
            });
    }

    public loadMoreItems(type: TransitNetworkPartType) {
        const loadMoreAmount = this.searchUtil.getSearchResultsLimit(this.env);

        const resultType = {
            [TransitNetworkPartType.Stop]: 'stopRoutes',
            [TransitNetworkPartType.Route]: 'stopRoutes',
            [TransitNetworkPartType.Location]: 'addresses',
            [TransitNetworkPartType.POI]: 'poi'
        }[type];

        if (!resultType) {
            return;
        }

        const currentItems = this.searchResultItems[resultType].length;

        const newItems = this.sortedResult[resultType]
            .slice(currentItems, currentItems + loadMoreAmount)
            .filter(
                (item: StopRouteResult | AddressResult | PoiAddressResult) =>
                    !this.searchResultItems[resultType].includes(item)
            );

        this.searchResultItems[resultType].push(...newItems);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    onScroll() {
        this.keyboardService.onScroll();
    }
}
